import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Upload, FileSpreadsheet, CheckCircle, AlertCircle, Loader2, PenLine } from 'lucide-react';
import { analysesApi } from '@/lib/api';
import { toast } from '@/components/ui/sonner';
import ManualEntryForm from '@/components/ManualEntryForm';

export default function DataImportPage({ onImport, loading, existingData, analysisName }) {
  const [dragActive, setDragActive] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    if (!validTypes.includes(file.type) && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    setUploading(true);
    try {
      const res = await analysesApi.upload(file);
      setParsedData(res.data);
      toast.success(`Parsed ${res.data.sheets?.length || 0} sheets from ${file.name}`);
    } catch (e) {
      console.error('Upload error:', e);
      toast.error('Failed to parse file');
    } finally {
      setUploading(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    handleFile(file);
  }, [handleFile]);

  const handleImportClick = () => {
    if (parsedData?.data) {
      onImport(parsedData.data);
    }
  };

  const handleManualSave = (formData) => {
    onImport(formData);
  };

  const dataKeys = parsedData?.data ? Object.keys(parsedData.data) : [];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="import-page">
      <div>
        <h2 className="text-xl font-semibold text-zinc-100 font-['Manrope']">Import Financial Data</h2>
        <p className="text-sm text-zinc-400 mt-1">Upload an Excel spreadsheet or enter data manually</p>
      </div>

      <Tabs defaultValue="upload" className="w-full">
        <TabsList className="bg-zinc-800/60 border border-zinc-700/50">
          <TabsTrigger value="upload" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-300" data-testid="tab-upload">
            <Upload className="w-3.5 h-3.5 mr-1.5" /> Upload File
          </TabsTrigger>
          <TabsTrigger value="manual" className="data-[state=active]:bg-indigo-500/20 data-[state=active]:text-indigo-300" data-testid="tab-manual">
            <PenLine className="w-3.5 h-3.5 mr-1.5" /> Manual Entry
          </TabsTrigger>
        </TabsList>

        {/* Upload Tab */}
        <TabsContent value="upload" className="mt-4">
          <Card className="bg-zinc-900/60 border-zinc-800/60">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-zinc-300">Upload Spreadsheet</CardTitle>
            </CardHeader>
            <CardContent>
              <div
                onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={() => setDragActive(false)}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-lg p-10 text-center transition-all ${
                  dragActive ? 'border-indigo-500 bg-indigo-500/5' : 'border-zinc-700 hover:border-zinc-600'
                }`}
                data-testid="upload-dropzone"
              >
                {uploading ? (
                  <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
                    <p className="text-sm text-zinc-400">Parsing spreadsheet...</p>
                  </div>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-zinc-500 mx-auto mb-3" />
                    <p className="text-sm text-zinc-300 mb-1">Drop your Excel file here or click to browse</p>
                    <p className="text-xs text-zinc-500">Supports .xlsx files with deal tabs, P&L, and DCF data</p>
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={(e) => handleFile(e.target.files?.[0])}
                      className="hidden"
                      id="file-upload"
                      data-testid="file-input"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-4 border-zinc-700 text-zinc-300"
                      onClick={() => document.getElementById('file-upload').click()}
                      data-testid="browse-files-btn"
                    >
                      <FileSpreadsheet className="w-4 h-4 mr-2" /> Browse Files
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Parsed Data Preview */}
          {parsedData && (
            <Card className="bg-zinc-900/60 border-zinc-800/60 mt-4 animate-fade-in">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium text-zinc-300">Parsed Data Preview</CardTitle>
                  <Badge variant="outline" className="border-emerald-500/30 text-emerald-400">
                    <CheckCircle className="w-3 h-3 mr-1" /> {parsedData.sheets?.length || 0} sheets
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {parsedData.sheets?.map((s) => (
                      <Badge key={s} variant="secondary" className="text-xs bg-zinc-800 text-zinc-300">{s}</Badge>
                    ))}
                  </div>

                  <div className="border-t border-zinc-800/60 pt-3 space-y-2">
                    <p className="text-xs text-zinc-400 font-medium">Detected Data Sections:</p>
                    {dataKeys.map((key) => {
                      const labelMap = {
                        seller_deal: 'Seller Deal Scenario',
                        buyer_deal: 'Buyer Planned Offer',
                        comparable_deal: 'Comparable (CabinetIQ)',
                        pnl_data: 'Historical P&L Data',
                        sde_entries: 'SDE Add-Back Entries',
                        seller_balance_sheet: 'Seller Balance Sheet',
                        buyer_balance_sheet: 'Buyer Balance Sheet',
                        inventory_entries: 'Inventory Data',
                        dcf_assumptions: 'DCF Assumptions',
                        scenarios: 'Scenario Configurations',
                      };
                      return (
                        <div key={key} className="flex items-center gap-2 text-xs">
                          <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
                          <span className="text-zinc-300">{labelMap[key] || key}</span>
                          {Array.isArray(parsedData.data[key]) && (
                            <span className="text-zinc-500 font-mono">({parsedData.data[key].length} entries)</span>
                          )}
                        </div>
                      );
                    })}
                    {dataKeys.length === 0 && (
                      <div className="flex items-center gap-2 text-xs text-amber-400">
                        <AlertCircle className="w-3.5 h-3.5" />
                        No compatible data found in file
                      </div>
                    )}
                  </div>

                  <Button
                    className="w-full bg-indigo-600 hover:bg-indigo-500 text-white mt-4"
                    onClick={handleImportClick}
                    disabled={loading || dataKeys.length === 0}
                    data-testid="confirm-import-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Import & Calculate
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Manual Entry Tab */}
        <TabsContent value="manual" className="mt-4">
          <Card className="bg-zinc-900/60 border-zinc-800/60">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-zinc-300">Manual Data Entry</CardTitle>
                <Badge variant="outline" className="border-amber-500/30 text-amber-400 text-[10px]">
                  <PenLine className="w-3 h-3 mr-1" /> {existingData ? 'Edit Mode' : 'New Entry'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-zinc-500 mb-4">
                {existingData 
                  ? '✓ Existing data loaded. Edit any tab and click Save to update. Unchanged tabs will keep their values.'
                  : 'Enter data manually. Fill in only the tabs you need - empty tabs will use defaults.'}
              </p>
              <ManualEntryForm onSave={handleManualSave} loading={loading} existingData={existingData} analysisName={analysisName} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
